<template>
  <v-main class="student-calendar">
    <div class="student-calendar__inner">
      <header class="student-calendar__title">
        <div>
          <h1>Мій календар</h1>
          <p>{{ isGuest ? 'Демонстраційний розклад на навчальний тиждень' : 'Розклад уроків і навчальних подій' }}</p>
        </div>
        <button v-if="isGuest" type="button" class="student-calendar__demo-cta" @click="openDemoRequest">
          Залишити контакти
          <v-icon size="18">mdi-arrow-right</v-icon>
        </button>
      </header>

      <section class="student-calendar__toolbar" aria-label="Керування календарем">
        <div class="student-calendar__navigation">
          <button type="button" class="student-calendar__today" @click="setToday">
            {{ isGuest ? 'Демо-тиждень' : 'Сьогодні' }}
          </button>
          <button
            type="button"
            aria-label="Попередній період"
            :disabled="previousNavigationDisabled"
            @click="prev"
          >
            <v-icon size="22">mdi-chevron-left</v-icon>
          </button>
          <button
            type="button"
            aria-label="Наступний період"
            :disabled="nextNavigationDisabled"
            @click="next"
          >
            <v-icon size="22">mdi-chevron-right</v-icon>
          </button>
          <strong>{{ periodTitle }}</strong>
        </div>

        <div class="student-calendar__views" role="group" aria-label="Вигляд календаря">
          <button
            v-for="view in views"
            :key="view.value"
            type="button"
            :class="{ 'student-calendar__view--active': type === view.value }"
            @click="selectType(view.value)"
          >
            {{ view.label }}
          </button>
        </div>
      </section>

      <section
        class="student-calendar__card"
        :class="{
          'student-calendar__card--day': type === 'day',
          'student-calendar__card--agenda': type === 'week',
        }"
      >
        <v-progress-linear v-if="loading" class="theme-loader" indeterminate />

        <div v-if="error && !loading" class="student-calendar__error">
          <v-icon color="#df3f35" size="36">mdi-alert-circle-outline</v-icon>
          <h2>Не вдалося завантажити календар</h2>
          <p>Перевірте з’єднання та спробуйте ще раз.</p>
          <button type="button" @click="reload">Повторити</button>
        </div>

        <div v-if="ready && !error && type === 'day'" class="student-day">
          <div v-if="dayGroups.length" class="student-day__content">
            <section v-for="group in dayGroups" :key="group.key" class="student-day__group">
              <h2>{{ group.label }}</h2>
              <div class="student-day__list">
                <button
                  v-for="event in group.events"
                  :key="event.id"
                  type="button"
                  class="student-day__lesson"
                  :class="{
                    'student-day__lesson--current': group.key === 'current',
                    'student-day__lesson--completed': group.key === 'completed',
                  }"
                  @click="openEvent(event)"
                >
                  <span class="student-day__time">
                    <strong>{{ eventTime(event) }}</strong>
                    <small v-if="event.lessonNumber">{{ event.lessonNumber }} урок</small>
                  </span>
                  <span class="student-day__accent" :style="{ backgroundColor: getEventColor(event) }"></span>
                  <span class="student-day__details">
                    <strong>{{ event.name }}</strong>
                    <small>{{ event.topic || event.description || "Навчальна подія" }}</small>
                    <span class="student-day__tags">
                      <span v-if="hasHomework(event)">ДЗ</span>
                      <span v-if="event.grade && event.grade.value !== null" class="student-day__tag--grade">
                        Оцінка {{ event.grade.value }}
                      </span>
                      <span v-if="event.assessment && event.assessment.isAssessment" class="student-day__tag--assessment">
                        {{ event.assessment.label || "Контрольна" }}
                      </span>
                      <span v-if="hasHomework(event) && homeworkTheme(event.homework) === 'overdue'" class="student-day__tag--danger">
                        Прострочено
                      </span>
                      <span v-if="hasHomework(event) && homeworkTheme(event.homework) === 'rework'" class="student-day__tag--rework">
                        На доопрацювання
                      </span>
                    </span>
                  </span>
                  <v-icon class="student-day__arrow" color="#667085" size="22">mdi-chevron-right</v-icon>
                </button>
              </div>
            </section>
          </div>
          <div v-else class="student-day__empty">
            <span><v-icon class="student-calendar__empty-icon" size="32">mdi-calendar-blank-outline</v-icon></span>
            <h2>На цей день уроків немає</h2>
            <p>Оберіть іншу дату або перегляньте розклад на тиждень.</p>
            <button type="button" @click="selectType('week')">Переглянути тиждень</button>
          </div>
        </div>

        <template v-else-if="ready && !error">
          <div
            v-if="type === 'week'"
            class="student-calendar__agenda"
            aria-label="Події тижня"
          >
            <section
              v-for="group in weekAgendaGroups"
              :key="group.date"
              class="student-calendar__agenda-day"
            >
              <header>
                <strong>{{ group.weekday }}</strong>
                <span>{{ group.dateLabel }}</span>
                <small v-if="group.isToday">Сьогодні</small>
              </header>
              <div v-if="group.events.length" class="student-calendar__agenda-events">
                <button
                  v-for="event in group.events"
                  :key="event.id"
                  type="button"
                  class="student-calendar__agenda-event"
                  @click="openEvent(event)"
                >
                  <span
                    class="student-calendar__agenda-accent"
                    :style="{ backgroundColor: getEventColor(event) }"
                  ></span>
                  <span class="student-calendar__agenda-time">
                    {{ eventTime(event) }}
                  </span>
                  <span class="student-calendar__agenda-details">
                    <strong>{{ event.name }}</strong>
                    <small>{{ event.topic || event.description || "Навчальна подія" }}</small>
                    <span class="student-day__tags">
                      <span v-if="hasHomework(event)">ДЗ</span>
                      <span
                        v-if="event.grade && event.grade.value !== null"
                        class="student-day__tag--grade"
                      >
                        Оцінка {{ event.grade.value }}
                      </span>
                    </span>
                  </span>
                  <v-icon color="#7a8490" size="20">mdi-chevron-right</v-icon>
                </button>
              </div>
              <p v-else class="student-calendar__agenda-empty">Немає подій</p>
            </section>
          </div>

          <v-calendar
            ref="calendar"
            v-model="focus"
            class="student-calendar__grid"
            :class="{ 'student-calendar__grid--mobile-hidden': type === 'week' }"
            color="#23936f"
            event-text-color="#172033"
            :events="events"
            :event-color="getEventColor"
            :type="type"
            :weekdays="weekdays"
            :locale="locale"
            :first-interval="calendarFirstInterval"
            :interval-count="calendarIntervalCount"
            :interval-height="calendarIntervalHeight"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @change="updateRange"
          >
            <template v-slot:event="{ event }">
              <div class="student-calendar__event">
                <strong class="student-calendar__event-name">{{ event.name }}</strong>
                <span class="student-calendar__event-tags">
                  <span v-if="hasHomework(event)">ДЗ</span>
                  <span v-if="event.grade && event.grade.value !== null" class="student-calendar__event-grade">
                    {{ event.grade.value }}
                  </span>
                  <span v-if="event.assessment && event.assessment.isAssessment" class="student-calendar__event-assessment">
                    Контрольна
                  </span>
                </span>
              </div>
            </template>
          </v-calendar>
        </template>
      </section>
    </div>

    <div
      class="student-calendar__backdrop"
      :class="{ 'student-calendar__backdrop--open': drawerOpen }"
      @click="closeDetails"
    ></div>
    <aside
      class="student-calendar__drawer"
      :class="{ 'student-calendar__drawer--open': drawerOpen }"
      :aria-hidden="String(!drawerOpen)"
      aria-label="Деталі події"
    >
      <button
        ref="closeButton"
        type="button"
        class="student-calendar__drawer-close"
        aria-label="Закрити"
        @click="closeDetails"
      >
        <v-icon size="20">mdi-close</v-icon>
      </button>
      <div v-if="selectedEvent.id" class="student-calendar__drawer-body">
        <h2>{{ selectedEvent.name }}</h2>
        <p class="student-calendar__drawer-date">{{ selectedEventDate }}</p>

        <section v-if="selectedEvent.topic || selectedEvent.description">
          <h3>Тема уроку</h3>
          <p>{{ selectedEvent.topic || selectedEvent.description }}</p>
        </section>
        <section v-if="selectedEvent.teacher && selectedEvent.teacher.name">
          <h3>Вчитель</h3>
          <p>{{ selectedEvent.teacher.name }}</p>
        </section>
        <section v-if="hasHomework(selectedEvent)">
          <h3>Домашнє завдання</h3>
          <p>{{ plainHomework(selectedEvent.homework.text) }}</p>
          <span
            class="student-calendar__homework-status"
            :class="`student-calendar__homework-status--${homeworkTheme(selectedEvent.homework)}`"
          >
            {{ selectedEvent.homework.statusLabel }}
          </span>
        </section>
        <section v-if="selectedEvent.grade && selectedEvent.grade.value !== null">
          <h3>Результат</h3>
          <p class="student-calendar__grade">
            {{ selectedEvent.grade.value }}
            <small>із {{ selectedEvent.grade.maxValue || 12 }}</small>
          </p>
          <span v-if="selectedEvent.assessment && selectedEvent.assessment.isAssessment">
            {{ selectedEvent.assessment.label }}
          </span>
        </section>
        <section v-if="selectedEvent.comments && selectedEvent.comments.length">
          <h3>Коментар вчителя</h3>
          <p>{{ selectedEvent.comments[0].text }}</p>
        </section>
        <section v-if="selectedEvent.materials && selectedEvent.materials.length">
          <h3>Матеріали</h3>
          <a
            v-for="(material, index) in selectedEvent.materials"
            :key="`${material}-${index}`"
            :href="material"
            target="_blank"
            rel="noopener noreferrer"
          >
            Відкрити матеріал {{ selectedEvent.materials.length > 1 ? index + 1 : "" }}
          </a>
        </section>
        <button
          v-if="selectedEvent.lessonId"
          type="button"
          class="student-calendar__open-lesson"
          @click="openLesson(selectedEvent)"
        >
          Відкрити урок
          <v-icon size="20">mdi-arrow-right</v-icon>
        </button>
      </div>
    </aside>
  </v-main>
</template>

<script>
import CoursesItemsService from "@/services/CoursesItemsService";
import popupEnum from "@/enums/popupEnum";

const DEFAULT_TYPE = "week";
const DEFAULT_START = createDate(new Date());
const DEMO_YEAR = createDemoYear();
const DEMO_MONTH_START = `${DEMO_YEAR}-09-01`;
const DEMO_MONTH_END = `${DEMO_YEAR}-09-30`;
const DEMO_START = DEMO_MONTH_START;
const CALENDAR_STATE_KEY = "CalendateState";
const DEFAULT_EVENT_COLOR = "#23936f";
const NAMED_COLORS = {
  green: "#4caf50",
};

function createDate(date) {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function createDemoYear() {
  const now = new Date();
  return now.getMonth() <= 8 ? now.getFullYear() : now.getFullYear() + 1;
}

function softenColor(rawColor, whiteRatio = 0.68) {
  const color = NAMED_COLORS[rawColor] || rawColor || DEFAULT_EVENT_COLOR;
  const normalized = /^#[0-9a-f]{3}$/i.test(color)
    ? color.replace(/([0-9a-f])/gi, "$1$1")
    : color;

  if (!/^#[0-9a-f]{6}$/i.test(normalized)) return "#e9f5ef";

  const channels = [1, 3, 5].map((index) => parseInt(normalized.slice(index, index + 2), 16));
  const softened = channels.map((channel) =>
    Math.round(channel + (255 - channel) * whiteRatio)
      .toString(16)
      .padStart(2, "0")
  );
  return `#${softened.join("")}`;
}

class CalendarState {
  constructor() {
    this.type = DEFAULT_TYPE;
    this.start = DEFAULT_START;
  }

  init() {
    const serializedData = localStorage.getItem(CALENDAR_STATE_KEY);
    if (!serializedData) return;

    try {
      const data = JSON.parse(serializedData);
      if (["day", "week", "month"].includes(data.type)) this.type = data.type;
      if (/^\d{4}-\d{2}-\d{2}$/.test(data.start || "")) this.start = data.start;
    } catch (error) {
      localStorage.removeItem(CALENDAR_STATE_KEY);
    }
  }

  update() {
    localStorage.setItem(CALENDAR_STATE_KEY, JSON.stringify({ type: this.type, start: this.start }));
  }

  setType(type) { this.type = type; this.update(); }
  setStart(start) { this.start = start; this.update(); }
}

export default {
  name: "Calendar",
  data: () => ({
    calendarState: new CalendarState(),
    ready: false,
    loading: false,
    error: null,
    focus: DEFAULT_START,
    type: DEFAULT_TYPE,
    periodTitle: "",
    range: { start: DEFAULT_START, end: DEFAULT_START },
    drawerOpen: false,
    selectedEvent: {},
    events: [],
    views: [
      { value: "day", label: "День" },
      { value: "week", label: "Тиждень" },
      { value: "month", label: "Місяць" },
    ],
    weekdays: [1, 2, 3, 4, 5, 6, 0],
    locale: "uk",
  }),
  computed: {
    isGuest() {
      return this.$store.getters["UserStore/isGuest"]();
    },
    calendarNavigationDisabled() {
      return this.type !== "day" &&
        (!this.ready || this.loading || Boolean(this.error));
    },
    previousNavigationDisabled() {
      if (!this.isGuest) return this.calendarNavigationDisabled;
      const boundary = this.type === "day" ? this.focus : this.range.start;
      return !boundary || boundary <= DEMO_MONTH_START;
    },
    nextNavigationDisabled() {
      if (!this.isGuest) return this.calendarNavigationDisabled;
      const boundary = this.type === "day" ? this.focus : this.range.end;
      return !boundary || boundary >= DEMO_MONTH_END;
    },
    timedEvents() {
      return this.events.filter((event) => event.timed && event.start && event.end);
    },
    calendarFirstInterval() {
      if (!this.timedEvents.length) return 7;
      const earliestHour = Math.min(...this.timedEvents.map((event) => event.start.getHours()));
      return Math.min(7, earliestHour);
    },
    calendarEndHour() {
      if (!this.timedEvents.length) return 18;
      const latestHour = Math.max(...this.timedEvents.map((event) => {
        const end = event.end;
        return end.getHours() + (end.getMinutes() > 0 ? 1 : 0);
      }));
      return Math.max(18, latestHour);
    },
    calendarIntervalCount() {
      return Math.max(1, this.calendarEndHour - this.calendarFirstInterval);
    },
    calendarIntervalHeight() {
      return Math.max(32, Math.floor(520 / this.calendarIntervalCount));
    },
    selectedDayEvents() {
      return this.events
        .filter((event) => createDate(event.start) === this.focus)
        .sort((first, second) => first.start - second.start);
    },
    dayGroups() {
      const groups = [
        { key: "current", label: "Зараз", events: [] },
        { key: "upcoming", label: "Далі", events: [] },
        { key: "completed", label: "Завершені", events: [] },
      ];
      this.selectedDayEvents.forEach((event) => {
        const group = groups.find((item) => item.key === this.eventTiming(event));
        group.events.push(event);
      });
      return groups.filter((group) => group.events.length);
    },
    weekAgendaGroups() {
      if (!this.range.start) return [];

      const firstDay = new Date(`${this.range.start}T12:00:00`);
      const today = createDate(new Date());
      return Array.from({ length: 7 }, (_, index) => {
        const day = new Date(firstDay);
        day.setDate(firstDay.getDate() + index);
        const date = createDate(day);
        return {
          date,
          weekday: new Intl.DateTimeFormat("uk-UA", { weekday: "long" }).format(day),
          dateLabel: new Intl.DateTimeFormat("uk-UA", {
            day: "numeric",
            month: "long",
          }).format(day),
          isToday: date === today,
          events: this.events
            .filter((event) => createDate(event.start) === date)
            .sort((first, second) => first.start - second.start),
        };
      });
    },
    selectedEventDate() {
      if (!this.selectedEvent.start) return "";
      return new Intl.DateTimeFormat("uk-UA", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(this.selectedEvent.start));
    },
  },
  async mounted() {
    if (this.isGuest) {
      this.type = "week";
      this.focus = DEMO_START;
      this.views = this.views.filter((view) => view.value !== "month");
      this.ready = true;
      await this.$nextTick();
      this.$refs.calendar.checkChange();
      document.addEventListener("keydown", this.onKeydown);
      return;
    }
    this.calendarState.init();
    this.type = this.calendarState.type;
    this.focus = this.calendarState.start;
    this.ready = true;
    await this.$nextTick();
    if (this.type === "day") {
      await this.loadDay(this.focus);
    } else {
      this.$refs.calendar.checkChange();
    }
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    selectType(type) {
      this.type = type;
      if (!this.isGuest) this.calendarState.setType(type);
      if (type === "day") {
        this.loadDay(this.focus);
      } else {
        this.$nextTick(this.refreshCalendar);
      }
    },
    viewDay({ date }) {
      if (
        this.isGuest &&
        (date < DEMO_MONTH_START || date > DEMO_MONTH_END)
      )
        return;
      this.focus = date;
      this.selectType("day");
    },
    getEventColor(event) { return softenColor(event.color); },
    setToday() {
      const target = this.isGuest ? DEMO_START : DEFAULT_START;
      this.focus = target;
      if (!this.isGuest) this.calendarState.setStart(target);
      if (this.type === "day") {
        this.loadDay(target);
      } else {
        this.$nextTick(this.refreshCalendar);
      }
    },
    prev() { this.movePeriod(-1); },
    next() { this.movePeriod(1); },
    movePeriod(direction) {
      if (this.type !== "day") {
        const calendar = this.$refs.calendar;
        if (!calendar) return;

        if (direction < 0) calendar.prev();
        else calendar.next();
        return;
      }
      const date = new Date(`${this.focus}T12:00:00`);
      date.setDate(date.getDate() + direction);
      this.focus = createDate(date);
      this.calendarState.setStart(this.focus);
      this.loadDay(this.focus);
    },
    refreshCalendar() { if (this.$refs.calendar) this.$refs.calendar.checkChange(); },
    openLesson(event) {
      this.closeDetails();
      this.$router.push({
        name: "lesson",
        params: { lessonId: event.lessonId },
        query: event.subject && event.subject.courseId
          ? { courseId: event.subject.courseId }
          : {},
      });
    },
    showEvent({ nativeEvent, event }) {
      this.openEvent(event);
      nativeEvent.stopPropagation();
    },
    openEvent(event) {
      this.selectedEvent = event;
      this.drawerOpen = true;
      document.body.style.overflow = "hidden";
      this.$nextTick(() => this.$refs.closeButton.focus());
    },
    closeDetails() {
      this.drawerOpen = false;
      document.body.style.overflow = "";
    },
    onKeydown(event) { if (event.key === "Escape" && this.drawerOpen) this.closeDetails(); },
    eventTime(event) {
      if (!event.startTime) return "Весь день";
      return [event.startTime, event.endTime].filter(Boolean).join("–");
    },
    eventTiming(event) {
      const selectedDate = createDate(event.start);
      const today = createDate(new Date());
      if (selectedDate < today) return "completed";
      if (selectedDate > today) return "upcoming";
      const now = new Date();
      if (now >= event.start && now <= event.end) return "current";
      return now > event.end ? "completed" : "upcoming";
    },
    hasHomework(event) {
      return Boolean(event.homework && event.homework.text && event.homework.text !== "Немає");
    },
    plainHomework(value) {
      const template = document.createElement("template");
      template.innerHTML = String(value || "").replace(/<br\s*\/?>/gi, "\n");

      return template.content.textContent.trim();
    },
    homeworkTheme(homework) {
      if (homework.needsRework) return "rework";
      if ([3, 6].includes(homework.status)) return "done";
      if (homework.status === 4) return "overdue";
      return "pending";
    },
    async updateRange({ start, end }) {
      this.range = { start: start.date, end: end.date };
      if (!this.isGuest) this.calendarState.setStart(start.date);
      this.periodTitle = this.formatPeriodTitle(start.date, end.date);
      await this.loadCalendar();
    },
    formatPeriodTitle(startValue, endValue) {
      const start = new Date(`${startValue}T12:00:00`);
      const end = new Date(`${endValue}T12:00:00`);

      if (this.type === "day") {
        return new Intl.DateTimeFormat("uk-UA", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        }).format(start);
      }

      if (this.type === "month") {
        return new Intl.DateTimeFormat("uk-UA", {
          month: "long",
          year: "numeric",
        }).format(new Date(`${this.focus}T12:00:00`));
      }

      const sameMonth = start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear();
      const sameYear = start.getFullYear() === end.getFullYear();

      if (sameMonth) {
        const endLabel = new Intl.DateTimeFormat("uk-UA", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }).format(end);
        return `${start.getDate()}–${endLabel}`;
      }

      const startLabel = new Intl.DateTimeFormat("uk-UA", {
        day: "numeric",
        month: "long",
        year: sameYear ? undefined : "numeric",
      }).format(start);
      const endLabel = new Intl.DateTimeFormat("uk-UA", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(end);
      return `${startLabel} – ${endLabel}`;
    },
    openDemoRequest() {
      this.$store.dispatch("PopupStore/openPopup", {
        popupName: popupEnum.DEMO_REQUEST_POPUP,
        popupContext: {
          courseName: "Навчальний календар",
          lessonName: "Повний кабінет учня",
        },
      });
    },
    async createDemoEvents() {
      let courses = this.$store.getters["CourseItemsStore/getCourses"];
      if (!courses.length) {
        await this.$store.dispatch("CourseItemsStore/getItems");
        courses = this.$store.getters["CourseItemsStore/getCourses"];
      }
      const sortedCourses = [...courses].sort((first, second) =>
        String(first.name || "").localeCompare(String(second.name || ""), "uk")
      );
      const courseLessons = await Promise.all(
        sortedCourses.map(async (course) => {
          try {
            const response = await CoursesItemsService.getLessons(course.id);
            const lessons = [...(response.data.lessons || [])].sort(
              (first, second) => (first.order || 0) - (second.order || 0)
            );
            return {
              course,
              lesson: lessons.find((lesson) => lesson.is_demo) || lessons[0],
            };
          } catch (error) {
            return null;
          }
        })
      );
      const availableCourses = courseLessons.filter(
        (item) => item && item.lesson
      );
      if (!availableCourses.length) return [];

      const slots = [
        { start: "09:00", end: "09:45" },
        { start: "10:00", end: "10:45" },
        { start: "11:00", end: "11:45" },
      ];
      const colors = ["#23936f", "#2d68bd", "#b3651f", "#7657a5", "#9a7200"];
      const monthStart = new Date(`${DEMO_MONTH_START}T12:00:00`);
      const monthEnd = new Date(`${DEMO_MONTH_END}T12:00:00`);
      const schoolDays = [];
      for (
        let date = new Date(monthStart);
        date <= monthEnd;
        date.setDate(date.getDate() + 1)
      ) {
        if (date.getDay() !== 0 && date.getDay() !== 6)
          schoolDays.push(new Date(date));
      }
      return schoolDays.map((date, dayIndex) =>
        slots.map((slot, slotIndex) => {
          const item = availableCourses[
            (dayIndex * slots.length + slotIndex) % availableCourses.length
          ];
          const dateValue = createDate(date);
          return {
            id: `demo-${dayIndex}-${slotIndex}`,
            name: item.course.name,
            title: item.course.name,
            topic: item.lesson.name,
            description: item.lesson.is_demo
              ? "Доступний демонстраційний урок"
              : "Урок із повної навчальної програми",
            date: dateValue,
            startTime: slot.start,
            endTime: slot.end,
            start: new Date(`${dateValue}T${slot.start}:00`),
            end: new Date(`${dateValue}T${slot.end}:00`),
            timed: true,
            lessonNumber: slotIndex + 1,
            lessonId: item.lesson.id,
            color: colors[(dayIndex + slotIndex) % colors.length],
            teacher: { name: "Команда RedFox" },
            subject: { name: item.course.name, courseId: item.course.id },
          };
        })
      ).flat();
    },
    async reload() { await this.loadCalendar(); },
    async loadDay(date) {
      this.range = { start: date, end: date };
      this.periodTitle = this.formatPeriodTitle(date, date);
      await this.loadCalendar();
    },
    async loadCalendar() {
      this.loading = true;
      this.error = null;
      try {
        if (this.isGuest) {
          this.events = await this.createDemoEvents();
          return;
        }
        await this.$store.dispatch("CalendarStore/loadCalendar", this.range);
        const calendar = this.$store.getters["CalendarStore/getCalendar"];
        this.events = calendar.map((event) => ({
          ...event,
          name: event.subject && event.subject.name ? event.subject.name : event.title,
          start: new Date(`${event.date}T${event.startTime || "00:00:00"}`),
          end: new Date(`${event.date}T${event.endTime || event.startTime || "23:59:59"}`),
          timed: event.allDay !== true && Boolean(event.startTime),
        }));
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.student-calendar { background: var(--theme-app-background-accent); min-height: 100vh; transition: background-color .2s ease; }
.student-calendar__inner { margin: 0 auto; max-width: 1480px; padding: 34px 28px 60px; }
.student-calendar__title { align-items: flex-end; display: flex; gap: 20px; justify-content: space-between; margin-bottom: 22px; }
.student-calendar__title h1 { color: #172033; font-size: 31px; margin: 0 0 7px; }
.student-calendar__title p { color: #667085; margin: 0; }
.student-calendar__demo-cta { align-items: center; background: transparent; border: 1px solid var(--theme-accent-decorative); border-radius: 9px; color: var(--theme-banner-text); display: flex; flex: 0 0 auto; font-size: 12px; font-weight: 800; gap: 7px; min-height: 40px; padding: 8px 12px; }
.student-calendar__demo-cta:hover { background: var(--theme-surface-decorative); }
.student-calendar__demo-cta::v-deep .v-icon { color: currentColor !important; }

.student-calendar__toolbar { align-items: center; background: var(--theme-banner-background); border: 1px solid var(--theme-banner-border); border-radius: 16px; box-shadow: 0 8px 30px rgba(31,41,55,.07); display: flex; justify-content: space-between; margin-bottom: 16px; padding: 14px 16px; transition: background-color .2s ease, border-color .2s ease; }
.student-calendar__navigation, .student-calendar__views { align-items: center; display: flex; gap: 7px; min-width: 0; }
.student-calendar__navigation button, .student-calendar__views button { align-items: center; background: transparent; border: 0; border-radius: 9px; color: #596574; cursor: pointer; display: inline-flex; font-weight: 750; justify-content: center; min-height: 40px; padding: 8px 11px; }
.student-calendar__navigation button:hover, .student-calendar__views button:hover { background: var(--theme-surface-decorative); }
.student-calendar__navigation button:disabled { cursor: default; opacity: .45; }
.student-calendar__navigation button:disabled:hover { background: transparent; }
.student-calendar__navigation strong { color: #172033; margin-left: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.student-calendar__navigation .student-calendar__today { background: var(--theme-surface-decorative); border: 1px solid var(--theme-accent-decorative); color: var(--theme-banner-text); padding: 8px 15px; }
.student-calendar__views { background: var(--theme-surface-soft); border-radius: 11px; padding: 4px; }
.student-calendar__views button { min-width: 78px; }
.student-calendar__views .student-calendar__view--active { background: var(--theme-header-background); box-shadow: 0 2px 7px rgba(31,41,55,.1); color: var(--theme-header-text); }

.student-calendar__card { background: var(--surface-primary); border: 1px solid var(--theme-border-decorative); border-radius: 16px; box-shadow: 0 8px 30px rgba(31,41,55,.07); min-height: 610px; overflow: hidden; padding: 16px; transition: border-color .2s ease; }
.student-calendar__card--day { min-height: 0; padding: 0; }
.student-calendar__grid { border: 1px solid var(--theme-border-decorative); border-radius: 12px; height: 610px; overflow: hidden; }
.student-calendar__grid::v-deep .v-calendar-weekly__head { background: var(--theme-surface-soft); }
.student-calendar__grid::v-deep .v-calendar-weekly__head-weekday { color: #465160; font-weight: 800; padding: 11px 4px; }
.student-calendar__grid::v-deep .v-calendar-weekly__day-label .v-btn { font-weight: 750; }
.student-calendar__grid::v-deep .v-calendar-weekly__day, .student-calendar__grid::v-deep .v-calendar-daily__day { border-color: var(--theme-border-decorative)!important; }
.student-calendar__grid::v-deep .v-calendar-daily__interval, .student-calendar__grid::v-deep .v-calendar-daily_head-day { border-color: var(--theme-border-decorative)!important; }
.student-calendar__grid::v-deep .v-calendar-weekly__day-label .v-btn:hover { background: var(--theme-surface-decorative)!important; color: var(--theme-banner-text)!important; }
.student-calendar__grid::v-deep .v-calendar-weekly__day-label .v-btn--active { background: var(--theme-header-background)!important; color: var(--theme-header-text)!important; }
.student-calendar__grid::v-deep .v-present { background-color: var(--theme-surface-decorative); }
.student-calendar__grid::v-deep .v-event { border: 0!important; border-left: 4px solid rgba(23,32,51,.16)!important; border-radius: 8px; box-shadow: 0 1px 3px rgba(31,41,55,.12); color: #172033!important; font-size: 12px; font-weight: 700; margin: 2px 3px!important; overflow: hidden; padding: 0; transition: box-shadow .15s, filter .15s, transform .15s; }
.student-calendar__grid::v-deep .v-event:hover { box-shadow: 0 4px 10px rgba(31,41,55,.16); filter: saturate(1.04); transform: translateY(-1px); }
.student-calendar__event { align-content: center; align-items: center; display: flex; gap: 5px; height: 100%; min-height: 25px; min-width: 0; overflow: hidden; padding: 5px 7px; }
.student-calendar__event-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.student-calendar__event-tags { display: inline-flex; flex: 0 0 auto; gap: 3px; }
.student-calendar__event-tags > span { background: rgba(255,255,255,.86); border: 1px solid rgba(45,104,189,.22); border-radius: 999px; color: #285f9f; font-size: 9px; line-height: 1; padding: 2px 5px; }
.student-calendar__event-tags .student-calendar__event-grade { border-color: rgba(35,147,111,.35); color: #176d50; }
.student-calendar__event-tags .student-calendar__event-assessment { border-color: rgba(196,116,15,.35); color: #a25a00; }
.student-calendar__agenda { display: none; }
.student-calendar__error { align-items: center; color: #667085; display: flex; flex-direction: column; justify-content: center; min-height: 600px; padding: 30px; text-align: center; }
.student-calendar__error h2 { color: #172033; margin: 14px 0 7px; }
.student-calendar__error p { margin: 0 0 18px; }
.student-calendar__error button { background: #23936f; border: 0; border-radius: 10px; color: #fff; cursor: pointer; font-weight: 800; min-height: 44px; padding: 10px 18px; }

.student-day { padding: 26px; }
.student-day__content { width: 100%; }
.student-day__group + .student-day__group { margin-top: 28px; }
.student-day__group h2 { color: #596574; font-size: 15px; letter-spacing: .02em; margin: 0 0 11px; text-transform: uppercase; }
.student-day__list { display: flex; flex-direction: column; gap: 10px; }
.student-day__lesson { align-items: stretch; background: #fff; border: 1px solid #e1e6ea; border-radius: 14px; box-shadow: 0 4px 14px rgba(31,41,55,.05); color: #172033; cursor: pointer; display: grid; grid-template-columns: 132px 7px minmax(0, 1fr) 34px; min-height: 104px; overflow: hidden; padding: 0; text-align: left; transition: border-color .15s, box-shadow .15s, transform .15s; width: 100%; }
.student-day__lesson:hover { border-color: var(--theme-accent-decorative); box-shadow: 0 8px 22px rgba(31,41,55,.09); transform: translateY(-1px); }
.student-day__lesson:focus-visible { outline: 3px solid var(--theme-accent-decorative); outline-offset: 2px; }
.student-day__lesson--current { border-color: var(--theme-accent-decorative); box-shadow: 0 7px 22px var(--theme-border-decorative); }
.student-day__lesson--completed { background: #fafbfc; }
.student-day__lesson--completed .student-day__details, .student-day__lesson--completed .student-day__time { opacity: .72; }
.student-day__time { align-items: flex-start; border-right: 1px solid #edf0f3; display: flex; flex-direction: column; justify-content: center; padding: 18px; }
.student-day__time strong { font-size: 16px; white-space: nowrap; }
.student-day__time small { color: #7a8490; margin-top: 5px; }
.student-day__accent { height: 100%; }
.student-day__details { align-self: center; display: block; min-width: 0; padding: 16px 20px; }
.student-day__details > strong, .student-day__details > small { display: block; }
.student-day__details > strong { font-size: 18px; }
.student-day__details > small { color: #667085; line-height: 1.4; margin-top: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.student-day__tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 11px; }
.student-day__tags > span { background: #eef4fc; border: 1px solid #cbdcf0; border-radius: 999px; color: #285f9f; font-size: 11px; font-weight: 800; padding: 4px 8px; }
.student-day__tags .student-day__tag--grade { background: #eaf6ef; border-color: #b9dec9; color: #24794e; }
.student-day__tags .student-day__tag--assessment { background: #fff3df; border-color: #f0c894; color: #a65b00; }
.student-day__tags .student-day__tag--danger { background: #ffe9e9; border-color: #f0b7b7; color: #b93131; }
.student-day__tags .student-day__tag--rework { background: #fff0dd; border-color: #eec99d; color: #a55400; }
.student-day__arrow { align-self: center; justify-self: center; }
.student-day__empty { align-items: center; color: #667085; display: flex; flex-direction: column; justify-content: center; min-height: 360px; padding: 36px; text-align: center; }
.student-day__empty > span { align-items: center; background: var(--theme-surface-decorative); border-radius: 16px; display: flex; height: 62px; justify-content: center; width: 62px; }
.student-calendar__empty-icon { color: var(--theme-banner-text) !important; }
.student-day__empty h2 { color: #172033; margin: 18px 0 7px; }
.student-day__empty p { margin: 0 0 20px; }
.student-day__empty button { background: var(--theme-header-background); border: 0; border-radius: 10px; color: var(--theme-header-text); cursor: pointer; font-weight: 800; min-height: 44px; padding: 10px 18px; }

.student-calendar__backdrop { background: rgba(18,28,39,.36); inset: 0; opacity: 0; pointer-events: none; position: fixed; transition: .2s; z-index: 60; }
.student-calendar__backdrop--open { opacity: 1; pointer-events: auto; }
.student-calendar__drawer { background: #fff; bottom: 0; box-shadow: -20px 0 50px rgba(20,31,45,.18); max-width: 100vw; overflow: auto; padding: 28px; position: fixed; right: 0; top: 0; transform: translateX(101%); transition: .25s; width: 560px; z-index: 61; }
.student-calendar__drawer--open { transform: translateX(0); }
.student-calendar__drawer-close { background: #f3f5f7; border: 0; border-radius: 12px; cursor: pointer; float: right; height: 44px; width: 44px; }
.student-calendar__drawer-body { padding-top: 58px; }
.student-calendar__drawer-body h2 { color: #172033; font-size: 30px; margin: 0 0 12px; }
.student-calendar__drawer-date { color: #566170; font-size: 17px; text-transform: capitalize; }
.student-calendar__drawer-body section { border-bottom: 1px solid #e6e9ee; border-top: 1px solid #e6e9ee; margin-top: 22px; padding: 18px 0; }
.student-calendar__drawer-body h3 { color: #6b7582; font-size: 16px; margin: 0 0 10px; }
.student-calendar__drawer-body section p { color: #303b4c; font-size: 17px; line-height: 1.55; margin: 0; white-space: pre-wrap; }
.student-calendar__drawer-body section a { color: #2d68bd; display: block; font-size: 16px; font-weight: 700; margin-top: 7px; }
.student-calendar__homework-status { border-radius: 999px; display: inline-flex; font-size: 12px; font-weight: 800; margin-top: 12px; padding: 6px 10px; }
.student-calendar__homework-status--done { background: #e8f6ee; color: #207149; }
.student-calendar__homework-status--pending { background: #fff3df; color: #a55d00; }
.student-calendar__homework-status--overdue { background: #ffe9e9; color: #b93131; }
.student-calendar__homework-status--rework { background: #fff0dd; color: #a55400; }
.student-calendar__drawer-body .student-calendar__grade { color: var(--theme-banner-text); font-size: 30px; font-weight: 900; }
.student-calendar__grade small { color: #667085; font-size: 14px; font-weight: 600; }
.student-calendar__open-lesson { align-items: center; background: var(--theme-header-background); border: 0; border-radius: 11px; color: var(--theme-header-text); cursor: pointer; display: flex; font-size: 16px; font-weight: 800; gap: 9px; justify-content: center; margin-top: 24px; min-height: 48px; padding: 11px 18px; width: 100%; }
.student-calendar__open-lesson::v-deep .v-icon { color: currentColor !important; }
.student-calendar__open-lesson:hover { filter: brightness(.9); }

@media(max-width: 760px) {
  .student-calendar__inner { padding: 24px 14px 40px; }
  .student-calendar__title h1 { font-size: 25px; }
  .student-calendar__title { align-items: stretch; flex-direction: column; }
  .student-calendar__demo-cta { justify-content: center; }
  .student-calendar__toolbar { align-items: stretch; flex-direction: column; gap: 12px; }
  .student-calendar__navigation { display: grid; grid-template-columns: auto 40px 40px minmax(0, 1fr); }
  .student-calendar__navigation strong { margin-left: 3px; text-align: right; }
  .student-calendar__views { display: grid; grid-template-columns: repeat(3, 1fr); }
  .student-calendar__views button { min-width: 0; }
  .student-calendar__card, .student-calendar__grid { min-height: 560px; }
  .student-calendar__card--day { min-height: 0; }
  .student-calendar__card--agenda { min-height: 0; padding: 0; }
  .student-calendar__grid { height: 560px; }
  .student-calendar__grid--mobile-hidden { display: none; }
  .student-calendar__agenda { display: block; }
  .student-calendar__agenda-day { padding: 17px 16px; }
  .student-calendar__agenda-day + .student-calendar__agenda-day { border-top: 1px solid #e8ebee; }
  .student-calendar__agenda-day > header { align-items: baseline; display: flex; gap: 8px; margin-bottom: 11px; text-transform: capitalize; }
  .student-calendar__agenda-day > header strong { color: #172033; font-size: 16px; }
  .student-calendar__agenda-day > header span { color: #697483; font-size: 13px; }
  .student-calendar__agenda-day > header small { background: #e8f6ef; border-radius: 999px; color: #207149; font-size: 10px; font-weight: 800; margin-left: auto; padding: 4px 7px; text-transform: uppercase; }
  .student-calendar__agenda-events { display: flex; flex-direction: column; gap: 8px; }
  .student-calendar__agenda-event { align-items: center; background: #fff; border: 1px solid #e1e6ea; border-radius: 12px; color: #172033; cursor: pointer; display: grid; gap: 10px; grid-template-columns: 5px 72px minmax(0, 1fr) 20px; min-height: 76px; overflow: hidden; padding: 0 10px 0 0; text-align: left; width: 100%; }
  .student-calendar__agenda-event:focus-visible { outline: 3px solid var(--theme-accent-decorative); outline-offset: 2px; }
  .student-calendar__agenda-accent { align-self: stretch; }
  .student-calendar__agenda-time { color: #465160; font-size: 12px; font-weight: 800; text-align: center; white-space: nowrap; }
  .student-calendar__agenda-details { min-width: 0; padding: 11px 0; }
  .student-calendar__agenda-details > strong, .student-calendar__agenda-details > small { display: block; }
  .student-calendar__agenda-details > strong { font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .student-calendar__agenda-details > small { color: #697483; font-size: 12px; margin-top: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .student-calendar__agenda-details .student-day__tags { margin-top: 7px; }
  .student-calendar__agenda-empty { color: #89919c; font-size: 13px; margin: 0; padding: 7px 0 3px 87px; }
  .student-day { padding: 14px; }
  .student-day__lesson { grid-template-columns: 96px 6px minmax(0, 1fr) 30px; min-height: 98px; }
  .student-day__time { padding: 13px; }
  .student-day__time strong { font-size: 13px; }
  .student-day__details { padding: 13px; }
  .student-day__details > strong { font-size: 16px; }
  .student-calendar__drawer { padding: 22px; width: 100%; }
  .student-calendar__drawer-body h2 { font-size: 26px; }
}

@media(max-width: 460px) {
  .student-calendar__navigation { grid-template-columns: auto 40px 40px; }
  .student-calendar__navigation strong { grid-column: 1 / -1; margin: 5px 0 0; text-align: left; }
  .student-calendar__agenda-event { grid-template-columns: 5px 60px minmax(0, 1fr) 18px; gap: 7px; }
  .student-calendar__agenda-empty { padding-left: 72px; }
  .student-day__lesson { grid-template-columns: 6px minmax(0, 1fr) 28px; }
  .student-day__time { border-bottom: 1px solid #edf0f3; border-right: 0; display: block; grid-column: 2; grid-row: 1; padding: 12px 12px 0; }
  .student-day__time small { display: inline; margin-left: 7px; }
  .student-day__accent { grid-column: 1; grid-row: 1 / span 2; }
  .student-day__details { grid-column: 2; grid-row: 2; padding-top: 9px; }
  .student-day__arrow { grid-column: 3; grid-row: 1 / span 2; }
}
</style>
